const EXAMPLE_FORMAT_CHECK_FIELD_NAMES = ['betLimit', 'gameInfo', 'gameForbidden']
const EXAMPLE_FORMAT_CHECK_FIELD_TYPES = ['array of strings', 'array of numbers']

const EXAMPLE_FORMAT_ERROR_MESSAGE_BY_FIELD_TYPE = {
  'array of strings': 'Please enter the correct format, such as: ["A", "B", "C"]',
  'array of numbers': 'Please enter the correct format, such as: [1, 2, 3]',
}

export const getExampleFormatErrorMessage = (field = {}) => (
  EXAMPLE_FORMAT_ERROR_MESSAGE_BY_FIELD_TYPE[field.fieldType]
  ?? 'Please enter the correct format'
)

export const shouldCheckExampleFormatError = (field = {}) => (
  EXAMPLE_FORMAT_CHECK_FIELD_NAMES.includes(field.fieldName)
  || EXAMPLE_FORMAT_CHECK_FIELD_TYPES.includes(field.fieldType)
)

export const normalizeExampleValueForInput = (value) => {
  if (value == null || value === '') return ''
  if (typeof value === 'string') return value
  if (typeof value === 'object') {
    if (Object.keys(value).length === 1 && 'arrayLength' in value) return ''
    try {
      return JSON.stringify(value)
    } catch {
      return ''
    }
  }
  return String(value)
}

const isNumberElement = value => typeof value === 'number' && !Number.isNaN(value)

export const isExampleFormatError = (value, field = {}, options = {}) => {
  const text = normalizeExampleValueForInput(value)
  if (!text) return false

  const { fieldType } = field
  const expectsArray = options.expectArray === true

  try {
    const parsed = JSON.parse(text)

    if (fieldType === 'array of strings') {
      if (expectsArray) {
        return !Array.isArray(parsed) || !parsed.every(item => typeof item === 'string')
      }
      return typeof parsed !== 'string'
    }

    if (fieldType === 'array of numbers') {
      if (expectsArray) {
        return !Array.isArray(parsed) || !parsed.every(isNumberElement)
      }
      return !isNumberElement(parsed)
    }

    return false
  } catch (error) {
    return true
  }
}

export const getSectionExampleIds = (sectionState = {}) => (
  Object.values(sectionState.examples || {}).map(({ exampleId }) => exampleId)
)

const isRootParentFieldId = parentFieldId => (
  parentFieldId === null || parentFieldId === undefined || parentFieldId === '' || parentFieldId === 'ROOT'
)

const findParentFieldInScope = (fieldData, scopeFields = {}) => {
  const parentFieldId = fieldData?.parentFieldId
  if (isRootParentFieldId(parentFieldId)) return null
  return scopeFields[parentFieldId]
    ?? Object.values(scopeFields).find(field => field?.fieldId === parentFieldId)
    ?? null
}

/** 與 FieldTable getExampleListForRecord 的 input key 對齊 */
export const resolveExampleFormatInputIdx = (fieldData, raw, scopeFields = {}, exampleId) => {
  const parent = findParentFieldInScope(fieldData, scopeFields)
  const parentArrayLength = parent?.fieldType === 'array of objects'
    ? parent.examples?.[exampleId]?.arrayLength
    : undefined
  const isParentArrayMultiItem = parentArrayLength != null && parentArrayLength >= 2
  const isArrayFieldType = EXAMPLE_FORMAT_CHECK_FIELD_TYPES.includes(fieldData.fieldType)
  const elementCheckOptions = isArrayFieldType ? { expectArray: false } : {}
  const wholeArrayCheckOptions = isArrayFieldType ? { expectArray: true } : {}

  if (Array.isArray(raw)) {
    if (!isParentArrayMultiItem) {
      if (isArrayFieldType) {
        const badIdx = raw.findIndex(cell => isExampleFormatError(cell, fieldData, elementCheckOptions))
        return badIdx !== -1 ? badIdx : null
      }
      return isExampleFormatError(raw[0], fieldData) ? 'scalar' : null
    }
    const badIdx = raw.findIndex(cell => isExampleFormatError(cell, fieldData, elementCheckOptions))
    return badIdx !== -1 ? badIdx : null
  }
  if (!isExampleFormatError(raw, fieldData, wholeArrayCheckOptions)) return null
  if (isParentArrayMultiItem) return 0
  return 'scalar'
}

export const buildExampleFormatInputKey = ({ fieldId, exampleId, idx }) => (
  `${fieldId}:${exampleId}:${idx}`
)

export const getExampleFormatInputKeyCandidates = (err) => {
  if (!err) return []
  const keys = [buildExampleFormatInputKey(err)]
  const base = `${err.fieldId}:${err.exampleId}`
  if (err.idx === 'scalar') keys.push(`${base}:0`)
  if (err.idx === 0 || err.idx === '0') keys.push(`${base}:scalar`)
  return [...new Set(keys)]
}

export const findExampleFormatErrorInScopeFields = (scopeFields = {}, exampleIds = []) => {
  for (const exampleId of exampleIds) {
    for (const fieldData of Object.values(scopeFields)) {
      if (!fieldData || fieldData.deleted || fieldData.isNew) continue
      if (!shouldCheckExampleFormatError(fieldData)) continue

      const raw = fieldData.examples?.[exampleId]
      const idx = resolveExampleFormatInputIdx(fieldData, raw, scopeFields, exampleId)
      if (idx !== null) return { fieldId: fieldData.fieldId, exampleId, idx }
    }
  }
  return null
}

export const findExampleFormatErrorInFieldState = ({
  selectedFieldState = {},
  selectedSectionState = {},
}) => {
  const exampleIds = getSectionExampleIds(selectedSectionState)
  const requestErr = findExampleFormatErrorInScopeFields(selectedFieldState.request, exampleIds)
  const responseErr = findExampleFormatErrorInScopeFields(selectedFieldState.response, exampleIds)
  return { requestErr, responseErr }
}
